import { useState, useCallback, useRef } from 'react';
import { DimensionamentoService } from '../../../services/DimensionamentoServices';
import { createInitialRows, processSpecialtyData } from '../utils/entryDataUtils';
import { convertFileToUtf8 } from '../../../utils/encodingUtils';
import Swal from 'sweetalert2';
import {LogsServices} from "../../../services/LogsServices";

/**
 * Custom hook for managing file upload functionality
 * @param {string} initialHeader - The initial header for the data
 * @param {Array} completeCities - Complete cities data
 * @param {Array} completeSpecialties - Complete specialties data
 * @param {Function} populateRows - Function to populate rows with data
 * @param {Function} setTableVersion - Function to update table version
 * @returns {Object} - Contains file state and upload handlers
 */
export const useFileUpload = (initialHeader, completeCities, completeSpecialties, populateRows, setTableVersion) => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const originalCache = useRef(null);

  const showValidRecordsDialog = useCallback(async (validAttributes) => {
    const validRows = validAttributes.map(item =>
      `<tr><td>${item.city.city} - ${item.city.state}</td><td>${item.attribute.name}</td><td>${item.value}</td><td>${item.networkAdjustmentFactor}%</td></tr>`
    ).join("");

    await Swal.fire({
      title: `Registros Válidos (${validAttributes.length})`,
      html: `<div style="max-height: 300px; overflow-y: auto;"><table border="1" cellpadding="5" cellspacing="0" style="width:100%; border-collapse: collapse;"><thead><tr><th>Cidade</th><th>Atributo</th><th>Valor</th><th>FAO</th></tr></thead><tbody>${validRows}</tbody></table></div>`,
      icon: "info",
      confirmButtonText: "Continuar",
      width: '700px'
    });
  }, []);

  const showInvalidRecordsDialog = useCallback(async (invalidRecords) => {
    const invalidRows = invalidRecords.map(msg => `<tr><td>${msg}</td></tr>`).join("");
    
    const result = await Swal.fire({
      title: `Registros Inválidos (${invalidRecords.length})`,
      html: `<div style="max-height: 300px; overflow-y: auto;"><table border="1" cellpadding="5" cellspacing="0" style="width:100%; border-collapse: collapse;"><thead><tr><th>Registros Inválidos</th></tr></thead><tbody>${invalidRows}</tbody></table></div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Usar apenas os válidos",
      cancelButtonText: "Cancelar upload",
      width: '700px'
    });

    return result.isConfirmed;
  }, []);

  const processUploadData = useCallback((validAttributes) => {
    if (!originalCache.current) {
      const initialRows = createInitialRows(completeSpecialties, completeCities.length + 1);
      originalCache.current = JSON.parse(JSON.stringify(initialRows));
    }

    const citiesByState = completeCities.reduce((acc, city) => {
      if (!acc[city.state]) acc[city.state] = [];
      acc[city.state].push(city);
      return acc;
    }, {});

    const sortedStates = Object.keys(citiesByState).sort();
    const organizedColumns = [initialHeader];

    sortedStates.forEach(state => {
      const sortedCities = citiesByState[state].sort((a, b) => a.city.localeCompare(b.city));
      sortedCities.forEach((city) => {
        organizedColumns.push(`${city.city} - ${city.state}`);
      });
    });

    const cityNames = organizedColumns;
    const rows = createInitialRows(completeSpecialties, cityNames.length);

    const transformedData = validAttributes.map(item => ({
      city: item.city,
      attribute: item.attribute,
      value: item.value,
      networkAdjustmentFactor: item.networkAdjustmentFactor
    }));

    const { hints, separationLines } = processSpecialtyData(completeSpecialties);
    
    populateRows(rows, cityNames, transformedData, hints, separationLines);
    setTableVersion(prev => prev + 1);
  }, [completeCities, completeSpecialties, initialHeader, populateRows, setTableVersion]);

  const handleFileUpload = useCallback(async (uploadedFile) => {
    if (!uploadedFile) return;
    
    setFile(uploadedFile);
    setIsUploading(true);

    try {
      const utf8File = await convertFileToUtf8(uploadedFile);
      
      if (utf8File !== uploadedFile) {
        await Swal.fire({
          title: "Conversão de Codificação",
          text: "O arquivo foi automaticamente convertido para UTF-8 para garantir a exibição correta dos caracteres.",
          icon: "info",
          confirmButtonText: "Continuar",
          timer: 4000,
          timerProgressBar: true
        });
      }

      LogsServices.postLog(`Upload: Upload de arquivo na etapa ${initialHeader}`);
      const uploadResponse = await DimensionamentoService.uploadFileDataCollect(initialHeader, utf8File);
      const { invalidRecords = [], validAttributes = [] } = uploadResponse;

      const filteredValidAttributes = validAttributes.filter(item => {
        return !(item.value === 0 && item.networkAdjustmentFactor === 0);
      });
      LogsServices.postLog(`Upload: Arquivo processado na etapa ${initialHeader} com ${filteredValidAttributes.length} registros válidos e ${invalidRecords.length} inválidos`);

      if (filteredValidAttributes.length > 0) {
        await showValidRecordsDialog(filteredValidAttributes);
      }

      if (invalidRecords.length > 0) {
        const shouldContinue = await showInvalidRecordsDialog(invalidRecords);
        if (!shouldContinue) {
          setFile(null);
          return;
        }
      }

      if (filteredValidAttributes.length === 0) {
        await Swal.fire({
          title: "Nenhum dado válido encontrado",
          text: "O arquivo não contém registros válidos para serem carregados.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      processUploadData(filteredValidAttributes);

      await Swal.fire({
        title: "Upload concluído",
        text: "Os dados válidos foram carregados na tabela.",
        icon: "success",
        confirmButtonText: "OK",
      });

      setTimeout(() => setFile(null), 0);

    } catch (error) {
      await Swal.fire({
        title: "Erro ao processar o arquivo",
        text: "Verifique o formato do arquivo e tente novamente.",
        icon: "error",
        confirmButtonText: "OK",
      });
      console.error("Erro ao processar o arquivo:", error);
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  }, [initialHeader, showValidRecordsDialog, showInvalidRecordsDialog, processUploadData]);

  const clearFile = useCallback(() => {
    setFile(null);
  }, []);

  return {
    file,
    isUploading,
    handleFileUpload,
    clearFile
  };
};
